import { useEffect } from 'react'
import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { isEmpty, findIndex } from 'lodash-es'
import { useTranslation } from 'next-i18next'
import { useAppSelector } from '@/redux/hooks'

// Libs
import { useShop } from '@/hooks/useShop'
import { useLiveData } from '@/hooks/useLiveData'
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'
import { handleGtmClickRoomListType } from '@/methods/handleGtmEvent'

// Components
import ProductContent from './component/ProductContent'
import LiveContent from './component/LiveContent'
import Tabs from '@/components/shared/Tabs'
import GoBackNavi from '@/components/shared/GoBackNavi'
import Merchant from '@/components/shared/Merchant'

// Constants
import { ROLE_TYPE } from '@/constants/roleType'
import { ROOM_LIVE_TYPE } from '@/constants/liveRoom'
import { GTM_LOCATION, GtmClickRoomListTabType } from '@/constants/gtmEvent'
import { ROUTE_PATH } from '@/constants/routePath'

// Styles
import styles from './style.module.scss'

type UserProps = {
  memberInfoId?: string
  isDrawer?: boolean
}

const cx = classnames.bind(styles)

const TAB_TYPE = {
  FUTURE_LIVE: '0',
  PAST_LIVE: '1',
  SHOP: '2',
}

const shopTabsData = [
  { role: ROLE_TYPE.KOL, label: 'Common_Tab_Live', value: TAB_TYPE.FUTURE_LIVE, gtmClickRoomListType: GtmClickRoomListTabType.LIVE },
  { role: ROLE_TYPE.KOL, label: 'Common_Tab_Past', value: TAB_TYPE.PAST_LIVE, gtmClickRoomListType: GtmClickRoomListTabType.VIDEO },
  { role: ROLE_TYPE.MERCHANT, label: 'Common_Tab_Merchant', value: TAB_TYPE.SHOP },
]

const User = (props: UserProps) => {
  const { memberInfoId, isDrawer } = props

  const { t } = useTranslation()

  const { merchantId, headshotUrl, profileName, roles, intro, refCode, totalProduct } = useShop({ memberInfoId })
  const previousPage = useAppSelector((state) => state.common.previousPage)
  const tabsData = shopTabsData.filter((data) => roles?.includes(data.role))

  const { isMerchantLive, roomId, liveMerchantInfo } = useLiveData({ merchantId: merchantId })

  const router = useRouter()
  const { query } = useRouter()
  const activeIndex = query?.type ? tabsData.findIndex((tab: any) => tab.value === query?.type) : 0

  useEffect(() => {
    if (!isEmpty(query) && !query?.type) {
      handleRoutePushWithQuery({
        router,
        pathname: router.asPath,
        query: {
          type: tabsData[0]?.value,
        },
        isReplace: true,
      })

      return
    }

    if (!query?.type || tabsData.length <= 0) return

    const defaultTab = findIndex(tabsData, (obj: any) => obj.value.includes(query?.type))

    if (defaultTab < 0 && tabsData.length > 0) {
      handleRoutePushWithQuery({
        router,
        pathname: router.asPath,
        query: {
          type: tabsData[0]?.value,
        },
      })
    }
  }, [tabsData, query.type])

  const handleTabSwitch = (type: string) => {
    handleRoutePushWithQuery({
      router,
      pathname: router.asPath,
      query: {
        type: type,
      },
    })
    const tabData = tabsData.find((tab: any) => tab.value === type)
    tabData?.gtmClickRoomListType && handleGtmClickRoomListType({ tabType: tabData?.gtmClickRoomListType, location: GTM_LOCATION.USER_PROFILE })
  }

  return (
    <div className={cx('user', { isDrawer })}>
      <div className={cx('container')}>
        {!isDrawer && <GoBackNavi title={previousPage ? t('Common_Back_Previous') : t('Common_Back_Mall')} staticLink={ROUTE_PATH.MALL} />}

        <Merchant
          headshotUrl={headshotUrl}
          profileName={profileName}
          totalProduct={totalProduct}
          intro={intro}
          refCode={refCode}
          isMerchantLive={isMerchantLive}
          roomId={roomId}
          roomData={liveMerchantInfo}
        />

        <Tabs className={cx('user-tabs')} values={tabsData} onChange={handleTabSwitch} activeIndex={activeIndex} />

        {query?.type == TAB_TYPE.FUTURE_LIVE && roles?.includes(ROLE_TYPE.KOL) && (
          <LiveContent
            memberInfoId={memberInfoId || query?.id}
            liveTypes={`${ROOM_LIVE_TYPE.CURRENT},${ROOM_LIVE_TYPE.FUTURE}`}
            isDrawer={isDrawer}
          />
        )}
        {query?.type == TAB_TYPE.PAST_LIVE && roles?.includes(ROLE_TYPE.KOL) && (
          <LiveContent memberInfoId={memberInfoId || query?.id} liveTypes={`${ROOM_LIVE_TYPE.PAST}`} isDrawer={isDrawer} />
        )}
        {query?.type == TAB_TYPE.SHOP && roles?.includes(ROLE_TYPE.MERCHANT) && <ProductContent merchantId={merchantId} isDrawer={isDrawer} />}
      </div>
    </div>
  )
}

export default User
